import React from 'react'
import { Link } from 'react-router-dom'
import { Users, Truck, Plus, TrendingUp, Clock, IndianRupee, UserCheck } from 'lucide-react'
import { useData } from '../context/DataContext'

function Dashboard() {
  const { state } = useData()
  const { customers, vehicles, workEntries, drivers } = state

  const stats = [
    {
      name: 'Total Customers',
      value: customers.length,
      icon: Users,
      color: 'bg-gradient-to-r from-brand-orange to-primary-600',
      link: '/customers'
    },


    {
      name: 'Total Work Hours',
      value: workEntries.reduce((sum, entry) => sum + parseFloat(entry.hoursWorked || 0), 0).toFixed(1),
      icon: Clock,
      color: 'bg-gradient-to-r from-brand-orange to-brand-red',
      link: '/work'
    },
    {
      name: 'Total Amount',
      value: `₹${workEntries.reduce((sum, entry) => {
        const baseAmount = parseFloat(entry.amount || 0)
        const bettaAmount = entry.workItems ? entry.workItems.reduce((itemSum, item) => 
          itemSum + parseFloat(item.betaAmount || 0), 0) : 0
        const taxAmount = parseFloat(entry.taxAmount || 0)
        const totalAmount = baseAmount + bettaAmount + taxAmount
        return sum + totalAmount
      }, 0).toLocaleString()}`,
      icon: IndianRupee,
      color: 'bg-gradient-to-r from-brand-orange to-primary-600',
      link: '/work'
    },
    {
      name: 'Given Amount',
      value: `₹${workEntries.reduce((sum, entry) => {
        const paidAmount = parseFloat(entry.paidAmount || 0)
        const initialPayment = parseFloat(entry.initialPayment || 0)
        return sum + paidAmount + initialPayment
      }, 0).toLocaleString()}`,
      icon: IndianRupee,
      color: 'bg-gradient-to-r from-primary-500 to-brand-red',
      link: '/work'
    },
    {
      name: 'Pending Amount',
      value: `₹${workEntries.reduce((sum, entry) => {
        const baseAmount = parseFloat(entry.amount || 0)
        const bettaAmount = entry.workItems ? entry.workItems.reduce((itemSum, item) => 
          itemSum + parseFloat(item.betaAmount || 0), 0) : 0
        const taxAmount = parseFloat(entry.taxAmount || 0)
        const totalAmount = baseAmount + bettaAmount + taxAmount
        const paidAmount = parseFloat(entry.paidAmount || 0)
        const pendingAmount = totalAmount - paidAmount
        
        // Only add to pending if there's actually a pending amount
        return sum + Math.max(0, pendingAmount)
      }, 0).toLocaleString()}`,
      icon: IndianRupee,
      color: 'bg-gradient-to-r from-orange-500 to-orange-600',
      link: '/work'
    }
  ]

  const recentCustomers = customers.slice(-5).reverse()
  const recentVehicles = vehicles.slice(-5).reverse()
  const recentDrivers = drivers.slice(-5).reverse()

  return (
    <div className="space-y-6 sm:space-y-8 px-4 sm:px-0">
      {/* Header */}
      <div className="text-center px-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
          Welcome to Sri Velmurugan ERP
        </h1>
        <p className="text-sm sm:text-base text-gray-600">
          Manage your transport and earthmoving operations efficiently
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {stats.map((stat) => {
          const Icon = stat.icon
          return (
            <Link
              key={stat.name}
              to={stat.link}
              className="card p-3 sm:p-4 hover:shadow-md transition-shadow duration-200"
            >
              <div className="flex items-center">
                <div className={`p-2 rounded-lg ${stat.color} flex-shrink-0`}>
                  <Icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div className="ml-2 sm:ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-600 truncate">{stat.name}</p>
                  <p className="text-base sm:text-lg font-semibold text-gray-900 truncate">{stat.value}</p>
                </div>
              </div>
            </Link>
          )
        })}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="card p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/customers"
              className="flex items-center justify-between p-3 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg hover:from-primary-100 hover:to-primary-200 transition-all duration-200 border border-primary-200"
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-brand-orange flex-shrink-0" />
                <span className="font-medium text-brand-red text-sm sm:text-base">Add New Customer</span>
              </div>
              <Plus className="h-4 w-4 text-brand-orange flex-shrink-0" />
            </Link>
            <Link
              to="/vehicles"
              className="flex items-center justify-between p-3 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg hover:from-primary-100 hover:to-primary-200 transition-all duration-200 border border-primary-200"
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Truck className="h-4 w-4 sm:h-5 sm:w-5 text-brand-orange flex-shrink-0" />
                <span className="font-medium text-brand-red text-sm sm:text-base">Add New Vehicle</span>
              </div>
              <Plus className="h-4 w-4 text-brand-orange flex-shrink-0" />
            </Link>
            <Link
              to="/work"
              className="flex items-center justify-between p-3 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg hover:from-primary-100 hover:to-primary-200 transition-all duration-200 border border-primary-200"
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-brand-orange flex-shrink-0" />
                <span className="font-medium text-brand-red text-sm sm:text-base">Add Work Entry</span>
              </div>
              <Plus className="h-4 w-4 text-brand-orange flex-shrink-0" />
            </Link>
            <Link
              to="/drivers"
              className="flex items-center justify-between p-3 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg hover:from-primary-100 hover:to-primary-200 transition-all duration-200 border border-primary-200"
            >
              <div className="flex items-center space-x-2 sm:space-x-3">
                <UserCheck className="h-4 w-4 sm:h-5 sm:w-5 text-brand-orange flex-shrink-0" />
                <span className="font-medium text-brand-red text-sm sm:text-base">Add New Driver</span>
              </div>
              <Plus className="h-4 w-4 text-brand-orange flex-shrink-0" />
            </Link>
          </div>
        </div>

        <div className="card p-4 sm:p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">System Overview</h3>
          <div className="space-y-3">
                        <div className="flex justify-between items-center">
               <span className="text-gray-600">Total Customers</span>
              <span className="font-semibold text-gray-900">{customers.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Vehicles</span>
              <span className="font-semibold text-gray-900">{vehicles.length}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total Drivers</span>
              <span className="font-semibold text-gray-900">{drivers.length}</span>
            </div>
            <div className="flex justify-between items-center">
               <span className="text-gray-600">Total Work Entries</span>
              <span className="font-semibold text-gray-900">{workEntries.length}</span>
            </div>
            <div className="flex justify-between items-center">
               <span className="text-gray-600">Total Services</span>
               <span className="font-semibold text-gray-900">{state.services ? state.services.length : 0}</span>
             </div>
             <div className="flex justify-between items-center">
               <span className="text-gray-600">Total Fuel Entries</span>
               <span className="font-semibold text-gray-900">{state.fuelEntries ? state.fuelEntries.length : 0}</span>
             </div>
             <div className="flex justify-between items-center">
               <span className="text-gray-600">Total Rentals</span>
               <span className="font-semibold text-gray-900">{state.rentals ? state.rentals.length : 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Recent Customers */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Customers</h3>
            <Link to="/customers" className="text-brand-orange hover:text-brand-red text-sm font-medium">
              View All
            </Link>
          </div>
          {recentCustomers.length > 0 ? (
            <div className="space-y-3">
              {recentCustomers.map((customer) => (
                <div key={customer.id} className="flex items-center space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="bg-primary-100 p-2 rounded-full flex-shrink-0">
                    <Users className="h-4 w-4 text-brand-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{customer.name}</p>
                    <p className="text-sm text-gray-500 truncate">{customer.phone}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No customers added yet</p>
          )}
        </div>

        {/* Recent Vehicles */}
        <div className="card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Vehicles</h3>
            <Link to="/vehicles" className="text-brand-orange hover:text-brand-red text-sm font-medium">
              View All
            </Link>
          </div>
          {recentVehicles.length > 0 ? (
            <div className="space-y-3">
              {recentVehicles.map((vehicle) => (
                <div key={vehicle.id} className="flex items-center space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="bg-primary-100 p-2 rounded-full flex-shrink-0">
                    <Truck className="h-4 w-4 text-brand-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{vehicle.registrationNumber}</p>
                    <p className="text-sm text-gray-500 truncate">{vehicle.type} - {vehicle.model}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No vehicles added yet</p>
          )}
        </div>

        {/* Recent Drivers */}
        <div className="card p-4 sm:p-6 md:col-span-2 lg:col-span-1">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Drivers</h3>
            <Link to="/drivers" className="text-brand-orange hover:text-brand-red text-sm font-medium">
              View All
            </Link>
          </div>
          {recentDrivers.length > 0 ? (
            <div className="space-y-3">
              {recentDrivers.map((driver) => (
                <div key={driver.id} className="flex items-center space-x-2 sm:space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="bg-primary-100 p-2 rounded-full flex-shrink-0">
                    <UserCheck className="h-4 w-4 text-brand-orange" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{driver.name}</p>
                    <p className="text-sm text-gray-500 truncate">{driver.phone_number}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No drivers added yet</p>
          )}
        </div>
      </div>
    </div>
  )
}

export default Dashboard
